import React from 'react';
import { makeStyles } from '@material-ui/styles';
import cn from 'clsx';
import { IconProps } from 'common/icons';
import ButtonBase from '../BaseButton';
import { Theme } from '../../../theme/types';

const IconButton: React.VFC<ButtonProps> = props => {
    const { size, icon: Icon, className, variant = 'default', ...domProps } = props;
    const classes = useStyles();
    const classNames = cn(classes.root, size && classes[size], className, classes[variant]);
    return (
        <ButtonBase className={classNames} {...domProps}>
            <Icon />
        </ButtonBase>
    );
};

const useStyles = makeStyles(
    ({ palette, mixBlendMode, customization }: Theme) => {
        const disabledFill = {
            cursor: 'not-allowed',
            pointerEvents: 'none',
            backgroundColor: palette.greys[400],
        };

        const disabledStroke = {
            pointerEvents: 'none',
            color: palette.greys[400],
            border: `1px solid ${palette.greys[400]}`,
        };
        return {
            root: {
                fontSize: '2rem',
                transition: 'all 300ms ease-in-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '3.6rem',
                height: '3.6rem',
                borderRadius: '50%',
                '&:disabled': {
                    pointerEvents: 'none',
                },
            },
            small: {
                fontSize: '1.6rem',
                width: '2.4rem',
                height: '2.4rem',
            },
            default: {
                color: palette.greys[700],
                backgroundColor: palette.neutral[100],
                mixBlendMode: mixBlendMode,
                '&:hover': {
                    backgroundColor: palette.neutral[300],
                },

                '&:disabled': {
                    color: palette.neutral[100],
                    backgroundColor: palette.greys[400],
                },
            },
            fillPrimary: {
                color: customization.interactive.G10,
                backgroundColor: customization.interactive.C10,
                '&:hover': {
                    color: customization.interactive.G10,
                    backgroundColor: customization.interactive.C20,
                },
                '&:disabled': disabledFill,
            },
            stroke: {
                color: palette.greys[700],
                border: `1px solid ${palette.greys[700]}`,
                '&:hover': {
                    backgroundColor: `${palette.greys[700]}10`,
                    mixBlendMode: mixBlendMode,
                },
                '&:disabled': disabledStroke,
            },
        };
    },
    { classNamePrefix: 'IconButton' }
);

type ButtonProps = JSX.IntrinsicElements['button'] & {
    size?: 'small';
    icon: React.VFC<IconProps>;
    variant?: 'fillPrimary' | 'stroke' | 'default';
};

export default IconButton;
